'use client'

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts'
import type { BarPayload } from '../svs-types'
import { resolveColor } from '../color-mapping'

interface BarRendererProps {
  payload: BarPayload
}

export function BarRenderer({ payload }: BarRendererProps) {
  const { categories, series } = payload.data
  const options = payload.options || {}
  const isHorizontal = options.orientation === 'horizontal'
  const showLegend = series.length > 1

  const data = categories.map((cat, i) => {
    const point: Record<string, unknown> = { name: cat }
    series.forEach((s, si) => {
      point[s.name || `series-${si}`] = s.values[i]
    })
    return point
  })

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data}
        layout={isHorizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        {isHorizontal ? (
          <>
            <XAxis type="number" label={options.xLabel ? { value: options.xLabel, position: 'insideBottom', offset: -5 } : undefined} />
            <YAxis type="category" dataKey="name" width={80} label={options.yLabel ? { value: options.yLabel, angle: -90, position: 'insideLeft' } : undefined} />
          </>
        ) : (
          <>
            <XAxis dataKey="name" label={options.xLabel ? { value: options.xLabel, position: 'insideBottom', offset: -5 } : undefined} />
            <YAxis label={options.yLabel ? { value: options.yLabel, angle: -90, position: 'insideLeft' } : undefined} />
          </>
        )}
        <Tooltip />
        {showLegend && <Legend />}
        {series.map((s, si) => {
          const key = s.name || `series-${si}`
          const color = resolveColor(s.color, si)
          return (
            <Bar
              key={key}
              dataKey={key}
              fill={color}
              stackId={options.stacked ? 'stack' : undefined}
              radius={[2, 2, 0, 0]}
            >
              {options.showValues && (
                <LabelList dataKey={key} position="top" className="fill-foreground text-xs" />
              )}
            </Bar>
          )
        })}
      </BarChart>
    </ResponsiveContainer>
  )
}
